import { Alert, Chip, IconButton, Spinner, Typography } from '@material-tailwind/react'
import { PiDetective, PiPencilSimpleLight } from 'react-icons/pi'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import { roleKey } from '../utils/constants/sharing.constants'

export interface Sharing {
  sharingId: string
  userEmail: string
  role: number
}

interface SharingListProps {
  sharingInfo?: { sharedWith?: Sharing[]; anonymouShareCount?: number } | null
  isLoading: boolean
  darkMode?: boolean
  onEdit: (sharing: Sharing) => void
}

const TABLE_HEAD = ['User', 'Role', '']

/**
 * Who an experience is shared with, plus the guest-access notice.
 *
 * Extracted from ShareExperiencePanel, which was already past the ~300 line limit
 * before this story added a role selector to it.
 *
 * Role labels are resolved by slug (`sharing.roles.listener.label`) and not by
 * number: a translator reads the name, and one key serves both this chip and the
 * dropdown.
 */
const SharingList: React.FC<SharingListProps> = ({ sharingInfo, isLoading, darkMode, onEdit }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="w-100 flex justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    )
  }

  if (!sharingInfo) {
    return (
      <div className="w-100 flex justify-center">
        <Typography className="italic">Could not load sharing info</Typography>
      </div>
    )
  }

  const { sharedWith = [], anonymouShareCount = 0 } = sharingInfo

  return (
    <>
      {sharedWith.length === 0 && anonymouShareCount === 0 && (
        <Typography variant="small" className="font-bold leading-none">
          {t('sharing.noSharingYet')}
        </Typography>
      )}

      {sharedWith.length > 0 && (
        <table className="w-full min-w-max table-auto text-left">
          <thead>
            <tr>
              {TABLE_HEAD.map((head) => (
                <th
                  key={head}
                  className="border-b border-blue-gray-100 bg-blue-gray-500/30 dark:bg-primary-600 p-4"
                >
                  <Typography variant="small" className="font-bold leading-none">
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sharedWith.map((sharing) => (
              <tr
                key={sharing.sharingId}
                data-testid="sharing-row"
                className="even:bg-blue-gray-50/50 dark:even:bg-primary-500"
              >
                <td className="p-4">
                  <Typography variant="small" color="blue-gray" className="font-normal">
                    {sharing.userEmail}
                  </Typography>
                </td>
                <td className="p-4">
                  <Typography variant="small" color="blue-gray" className="font-normal">
                    <Chip
                      className="inline-block"
                      data-testid="sharing-row-role"
                      value={t(roleKey(sharing.role, 'label'))}
                    />
                  </Typography>
                </td>
                <td className="p-4 text-right">
                  <IconButton
                    size="sm"
                    data-testid="sharing-edit-btn"
                    variant="text"
                    color="blue-gray"
                    onClick={() => onEdit(sharing)}
                  >
                    <PiPencilSimpleLight className="w-4 h-4" />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {anonymouShareCount > 0 && (
        <Alert
          color={darkMode ? 'blue-gray' : 'blue'}
          variant={darkMode ? 'filled' : 'ghost'}
          icon={<PiDetective className="w-6 h-6" />}
        >
          <Typography variant="h6" className="font-bold">
            {t('sharing.guestUsers', { count: anonymouShareCount })}
          </Typography>
          <Typography className="leading-tight mt-2 text-sm">
            <Trans
              i18nKey="sharing.guestDescription"
              count={anonymouShareCount}
              components={{
                Link: <Link to="/access-codes" />,
                underline: <span className="underline" />,
              }}
            />
          </Typography>
        </Alert>
      )}
    </>
  )
}

export default SharingList
